import type { StoreFrontDto } from '@b-stock/listing-api-client'

import type { ListingSearchState } from '@/components/listings/search/ListingSearchProvider'
import { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import { getSearchData } from '@/queries/listingSearchQuery'

import { fetchAllFilteredListingIds } from './fetchAllFilteredListingIds'

// Matches LISTING_LOOKUP_CHUNK_SIZE in UploadBidSheetModal.tsx — the same
// "look up many IDs" shape, so both paths hit the search endpoint the same way.
const LOOKUP_CHUNK_SIZE = 100

/**
 * Resolves every listing matching the current filter/search state, across
 * all pages, as fully augmented `CombinedListingSearchResult` objects (bid
 * amount, currency, title, etc.) — not just IDs.
 *
 * First resolves the full ID set via `fetchAllFilteredListingIds` (which also
 * enforces the export cap, throwing `ManifestExportCapExceededError` if the
 * filtered set is too large), then looks those IDs up in chunks the same way
 * `UploadBidSheetModal` already does for an uploaded sheet's rows, so a
 * downloaded bid sheet and an uploaded one resolve listing data identically.
 * Chunks are fetched in parallel — at the export cap that's up to 15 chunks,
 * and nothing about one chunk's result depends on another's. The id-based
 * lookup endpoint returns each chunk in its own response order, not the
 * `listingIds` order the grid's current sort already produced, so results
 * are re-sorted back into that order before returning.
 */
export async function fetchAllFilteredListings(
  accessToken: string | null,
  state: ListingSearchState,
  storefront: StoreFrontDto | undefined
): Promise<CombinedListingSearchResult[]> {
  const { listingIds } = await fetchAllFilteredListingIds(
    accessToken,
    state,
    storefront
  )
  if (!listingIds.length) return []

  const chunks: string[][] = []
  for (let i = 0; i < listingIds.length; i += LOOKUP_CHUNK_SIZE) {
    chunks.push(listingIds.slice(i, i + LOOKUP_CHUNK_SIZE))
  }

  const chunkResults = await Promise.all(
    chunks.map(async (chunkIds) => {
      const { listings: rawListings } = await getSearchData(accessToken, {
        params: { id: chunkIds, limit: chunkIds.length },
        usePostEndpoint: true,
        fetchFilters: false,
      })
      return rawListings.map((raw) => new CombinedListingSearchResult(raw))
    })
  )

  const listingsById = new Map(
    chunkResults.flat().map((listing) => [listing.listingId, listing])
  )
  return listingIds
    .map((id) => listingsById.get(id))
    .filter((listing): listing is CombinedListingSearchResult => !!listing)
}
